import { useState, useEffect } from 'react'
import { X, Save, Loader2, AlertCircle } from 'lucide-react'
import styles from './AddProductModal.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

// ── Option lists ─────────────────────────────────────────────────────────────

const ENVIRONMENTS = [
  { value: 'PRODUCTION', label: 'Production' },
  { value: 'UAT', label: 'UAT' },
  { value: 'QA', label: 'QA' },
  { value: 'DEV', label: 'Development' },
  { value: 'DEMO', label: 'Demo' },
]
const CRITICALITIES = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
]
const BUSINESS_IMPACTS = [
  { value: 'CATASTROPHIC', label: 'Catastrophic' },
  { value: 'MAJOR', label: 'Major' },
  { value: 'MODERATE', label: 'Moderate' },
  { value: 'MINOR', label: 'Minor' },
]
const DR_STRATEGIES = [
  { value: 'ACTIVE_ACTIVE', label: 'Active-Active' },
  { value: 'ACTIVE_PASSIVE', label: 'Active-Passive' },
  { value: 'PILOT_LIGHT', label: 'Pilot Light' },
  { value: 'WARM_STANDBY', label: 'Warm Standby' },
  { value: 'BACKUP_RESTORE', label: 'Backup & Restore' },
]
const AUTOMATION_OPTIONS = [
  { value: 'FULL', label: 'Fully Automated' },
  { value: 'PARTIAL', label: 'Partially Automated' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'NONE', label: 'Not Configured' },
]
const AZURE_REGIONS = [
  'East US', 'East US 2', 'West US', 'West US 2', 'West US 3',
  'Central US', 'South Central US', 'North Central US',
  'North Europe', 'West Europe', 'UK South', 'UK West',
  'France Central', 'Germany West Central',
  'Australia East', 'Australia Southeast',
  'Southeast Asia', 'East Asia',
  'Brazil South', 'Canada Central', 'Canada East',
  'Japan East', 'Japan West', 'Korea Central',
  'South Africa North', 'UAE North', 'Other / On-Premises',
]

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProductFormData {
  product_name: string
  product_code: string
  environment: string
  business_owner: string
  technical_owner: string
  cost_center: string
  primary_region: string
  dr_region: string
  azure_subscription_id: string
  resource_group: string
  criticality: string
  business_impact: string
  recovery_sequence: number
  dr_strategy: string
  automation_readiness: string
  rpo_target_hours: number
  rto_target_hours: number
  notes: string
}

const DEFAULT: ProductFormData = {
  product_name: '',
  product_code: '',
  environment: 'PRODUCTION',
  business_owner: '',
  technical_owner: '',
  cost_center: '',
  primary_region: '',
  dr_region: '',
  azure_subscription_id: '',
  resource_group: '',
  criticality: 'MEDIUM',
  business_impact: 'MODERATE',
  recovery_sequence: 100,
  dr_strategy: 'BACKUP_RESTORE',
  automation_readiness: 'NONE',
  rpo_target_hours: 4,
  rto_target_hours: 8,
  notes: '',
}

interface Props {
  initialData?: Partial<ProductFormData> & { id?: number }
  onSaved: () => void
  onClose: () => void
}

type Errors = Partial<Record<keyof ProductFormData, string>>

// ── Helper components ─────────────────────────────────────────────────────────

function Field({
  label, required, error, children,
}: {
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className={styles.field}>
      <label className={styles.label}>
        {label}
        {required && <span className={styles.required}> *</span>}
      </label>
      {children}
      {error && <div className={styles.fieldError}><AlertCircle size={12} /> {error}</div>}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function AddProductModal({ initialData, onSaved, onClose }: Props) {
  const isEdit = !!initialData?.id
  const [form, setForm] = useState<ProductFormData>({ ...DEFAULT, ...initialData })
  const [errors, setErrors] = useState<Errors>({})
  const [saving, setSaving] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  function set(field: keyof ProductFormData, value: string | number) {
    setForm(f => ({ ...f, [field]: value }))
    setErrors(e => ({ ...e, [field]: undefined }))
  }

  function validate(): boolean {
    const errs: Errors = {}
    if (!form.product_name.trim()) errs.product_name = 'Product name is required'
    if (form.rpo_target_hours <= 0) errs.rpo_target_hours = 'Must be > 0'
    if (form.rto_target_hours <= 0) errs.rto_target_hours = 'Must be > 0'
    if (form.rto_target_hours < form.rpo_target_hours) {
      errs.rto_target_hours = 'RTO must be ≥ RPO'
    }
    if (form.business_owner && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.business_owner)) {
      errs.business_owner = 'Enter a valid email'
    }
    if (form.technical_owner && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.technical_owner)) {
      errs.technical_owner = 'Enter a valid email'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setSaving(true)
    setApiError(null)
    try {
      const url = isEdit
        ? `${DR_API}/api/v1/dr/products/${initialData!.id}/`
        : `${DR_API}/api/v1/dr/products/`
      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        const msg = Object.values(body).flat().join(' ') || `Server error ${res.status}`
        setApiError(msg)
        return
      }
      onSaved()
    } catch {
      setApiError('Network error — check the DR API is running on port 8001.')
    } finally {
      setSaving(false)
    }
  }

  const inp = (field: keyof ProductFormData) => (
    `${styles.input} ${errors[field] ? styles.inputError : ''}`
  )

  return (
    <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>{isEdit ? 'Edit Product' : 'Add Product to DR Inventory'}</h2>
            <p className={styles.subtitle}>
              {isEdit ? 'Update DR configuration for this product.' : 'Register a new Aptean product with its DR strategy and recovery targets.'}
            </p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form}>
          {apiError && (
            <div className={styles.apiError}><AlertCircle size={14} /> {apiError}</div>
          )}

          {/* ── Section: Basic Information ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Basic Information</div>
            <div className={styles.grid2}>
              <Field label="Product Name" required error={errors.product_name}>
                <input
                  className={inp('product_name')}
                  value={form.product_name}
                  onChange={e => set('product_name', e.target.value)}
                  placeholder="e.g. Aptean Food &amp; Beverage ERP"
                  autoFocus
                />
              </Field>
              <Field label="Product Code" error={errors.product_code}>
                <input
                  className={inp('product_code')}
                  value={form.product_code}
                  onChange={e => set('product_code', e.target.value)}
                  placeholder="e.g. AFB-001"
                />
              </Field>
            </div>
            <div className={styles.grid2}>
              <Field label="Environment" required>
                <select className={styles.select} value={form.environment} onChange={e => set('environment', e.target.value)}>
                  {ENVIRONMENTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Cost Center">
                <input
                  className={inp('cost_center')}
                  value={form.cost_center}
                  onChange={e => set('cost_center', e.target.value)}
                  placeholder="e.g. CC-4210"
                />
              </Field>
            </div>
          </div>

          {/* ── Section: Ownership ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Ownership</div>
            <div className={styles.grid2}>
              <Field label="Business Owner (email)" error={errors.business_owner}>
                <input
                  className={inp('business_owner')}
                  type="email"
                  value={form.business_owner}
                  onChange={e => set('business_owner', e.target.value)}
                  placeholder="owner@aptean.com"
                />
              </Field>
              <Field label="Technical Owner (email)" error={errors.technical_owner}>
                <input
                  className={inp('technical_owner')}
                  type="email"
                  value={form.technical_owner}
                  onChange={e => set('technical_owner', e.target.value)}
                  placeholder="tech@aptean.com"
                />
              </Field>
            </div>
          </div>

          {/* ── Section: Infrastructure ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Azure Infrastructure</div>
            <div className={styles.grid2}>
              <Field label="Primary Region">
                <select className={styles.select} value={form.primary_region} onChange={e => set('primary_region', e.target.value)}>
                  <option value="">— select region —</option>
                  {AZURE_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="DR / Failover Region">
                <select className={styles.select} value={form.dr_region} onChange={e => set('dr_region', e.target.value)}>
                  <option value="">— select region —</option>
                  {AZURE_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
            </div>
            <div className={styles.grid2}>
              <Field label="Azure Subscription ID">
                <input
                  className={inp('azure_subscription_id')}
                  value={form.azure_subscription_id}
                  onChange={e => set('azure_subscription_id', e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
              </Field>
              <Field label="Resource Group">
                <input
                  className={inp('resource_group')}
                  value={form.resource_group}
                  onChange={e => set('resource_group', e.target.value)}
                  placeholder="e.g. rg-aptean-prod-eastus"
                />
              </Field>
            </div>
          </div>

          {/* ── Section: Classification ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Classification</div>
            <div className={styles.grid3}>
              <Field label="Criticality" required>
                <select className={styles.select} value={form.criticality} onChange={e => set('criticality', e.target.value)}>
                  {CRITICALITIES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Business Impact" required>
                <select className={styles.select} value={form.business_impact} onChange={e => set('business_impact', e.target.value)}>
                  {BUSINESS_IMPACTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Recovery Priority">
                <input
                  type="number"
                  className={inp('recovery_sequence')}
                  value={form.recovery_sequence}
                  min={1}
                  onChange={e => set('recovery_sequence', parseInt(e.target.value) || 100)}
                />
              </Field>
            </div>
          </div>

          {/* ── Section: DR Configuration ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>DR Configuration</div>
            <div className={styles.grid2}>
              <Field label="DR Strategy" required>
                <select className={styles.select} value={form.dr_strategy} onChange={e => set('dr_strategy', e.target.value)}>
                  {DR_STRATEGIES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Automation Readiness" required>
                <select className={styles.select} value={form.automation_readiness} onChange={e => set('automation_readiness', e.target.value)}>
                  {AUTOMATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
            </div>
          </div>

          {/* ── Section: RPO / RTO ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Recovery Targets</div>
            <div className={styles.grid2}>
              <Field label="RPO Target (hours)" required error={errors.rpo_target_hours}>
                <div className={styles.numberInput}>
                  <input
                    type="number"
                    className={inp('rpo_target_hours')}
                    value={form.rpo_target_hours}
                    min={0.25}
                    step={0.25}
                    onChange={e => set('rpo_target_hours', parseFloat(e.target.value) || 0)}
                  />
                  <span className={styles.inputSuffix}>hrs</span>
                </div>
                <div className={styles.fieldHint}>Maximum data loss tolerance</div>
              </Field>
              <Field label="RTO Target (hours)" required error={errors.rto_target_hours}>
                <div className={styles.numberInput}>
                  <input
                    type="number"
                    className={inp('rto_target_hours')}
                    value={form.rto_target_hours}
                    min={0.25}
                    step={0.25}
                    onChange={e => set('rto_target_hours', parseFloat(e.target.value) || 0)}
                  />
                  <span className={styles.inputSuffix}>hrs</span>
                </div>
                <div className={styles.fieldHint}>Maximum acceptable downtime</div>
              </Field>
            </div>
          </div>

          {/* ── Section: Notes ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Notes</div>
            <textarea
              className={styles.textarea}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Additional context, dependencies, special instructions..."
              rows={3}
            />
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? <><Loader2 size={14} className={styles.spin} /> Saving…</> : <><Save size={14} /> {isEdit ? 'Save Changes' : 'Add Product'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
